/**
 * Semrush Adapter — Story 20.4 (opens the Semrush slice of Epic 20)
 *
 * **Auth model:** Per-user API key sent as a `?key=<KEY>` query parameter.
 * The customer pastes their Semrush Business + API + Trends API key into
 * the Connect dialog; we encrypt it via the same `encryptToken` helper
 * that OAuth tokens use and store it in `oauth_tokens` under
 * `provider="semrush"`. See AC #3 in the Story 20.4 spec for why we
 * deliberately reuse the OAuth table for an API key.
 *
 * **DELIBERATE divergences from Ahrefs (Story 20.1) — internalize before
 * reaching for any Ahrefs file as a copy template:**
 *
 *  1. **Auth shape.** Semrush sends `?key=<KEY>` query param, NOT
 *     `Authorization: Bearer <KEY>` header. The literal key value appears
 *     in EVERY request URL — a production-grade leak vector through CDN
 *     logs, Cloudflare Workers metrics, and our own error-handler logs
 *     unless the safeFetch wrapper redacts at the log boundary. See
 *     `safe-fetch.ts:safeFetchSemrush` (Round 4 Red Team Attack #1).
 *
 *  2. **Response format.** Semrush Analytics API + Trends API return
 *     SEMICOLON-DELIMITED CSV (NOT JSON) per developer.semrush.com/api/v3/
 *     analytics/ verified 2026-05-08. The adapter ships its own
 *     `parseSemrushCsv` (in `csv.ts`) handling: semicolon delimiters; first-
 *     row headers; quoted fields when `export_escape=1`; URL-decoded values
 *     when `export_decode=1` (the default); plaintext-error detection
 *     (`/^ERROR \d+ ::/`).
 *
 *  3. **Unit-cost economics.** Semrush charges per-row for most reports +
 *     fixed-cost-per-request for backlinks_overview (40u) + 50u/row for
 *     phrase_kdi. Ahrefs's `select`-mandatory invariant doesn't apply —
 *     Semrush's `export_columns` narrows columns but unit cost depends on
 *     row count, not field count. The adapter clamps `display_limit` to
 *     1000 rows max (Round 1 SRE finding) + a per-tenant 200K-units/day
 *     circuit-breaker.
 *
 *  4. **30-day data persistence cap (CONTRACTUAL).** Per Semrush ToS §3.3
 *     verified 2026-05-08: "you agree not to cache the information received
 *     from the Services for more than one (1) month without the express
 *     written consent of Semrush." Encoded as
 *     `STALE_EXPIRATION_TTL_BY_PLATFORM["semrush"] = 2_592_000` in
 *     `cache.ts`. **This is a build-to-sell decision (legal-contract
 *     compliance), not just operational tuning.** A buyer's diligence team
 *     will look for evidence we read the ToS.
 *
 * **Trends API entitlement (AC #2, AC #14):** the Trends API is a SEPARATE
 * subscription with its own 10K-units/month quota. The adapter discovers
 * Trends entitlement at connect time (the route handler probes
 * `/summary` with display_limit=1) and surfaces "Trends API Required"
 * badges via `EndpointDiscoveryInfo` when not entitled.
 *
 * **`API_KEY_PLATFORMS` divergence (AC #11 + Task 1.10):** `"semrush"` is
 * NOT added to `API_KEY_PLATFORMS` in adapter.ts. That set is reserved for
 * env-binding API-key platforms (currently `["google-pagespeed"]`). Semrush
 * is per-user-secret like Ahrefs, NOT env-key. Document this divergence
 * here so a future agent doesn't try to "fix" it.
 *
 * **Future Semrush OAuth (AC #17, deferred to Story 20.4d+):** the Projects
 * + Local APIs support OAuth 2.0; Story 20.4 does NOT consume those
 * endpoints. The future story will land as a SEPARATE family entry (e.g.
 * `"semrush-oauth"`) with `accessTokenTtlSec: 3600` AND
 * `inlineRefreshOn401: true`. The API-key path stays untouched. Both will
 * share THIS adapter implementation because Semrush accepts the same
 * `?key=<KEY>` query parameter for OAuth access tokens too.
 *
 * ============================================================================
 * Autonomous sync surface vs. widget-config-driven surface — Story 20.5 boundary
 * ============================================================================
 *
 * **Autonomous sync (4 endpoints — Story 20.4):** the data-sync queue calls
 * `fetchMetrics` on a 4h cadence; that surface continues to fire ONLY the
 * original 4 endpoint groups:
 *   - `domain_ranks`        — safe defaults (target + database)
 *   - `backlinks_overview`  — safe defaults (target)
 *   - `phrase_kdi`          — gated on `trackedKeywords` metadata
 *   - Trends `/summary`     — gated on `trendsEntitled` metadata
 *
 * **Widget-config-driven (6 endpoints — Story 20.5):** the new endpoints
 * land ONLY in the proxy-route surface at
 * `apps/server/src/routes/integrations/semrush.ts`. They are NOT called from
 * autonomous sync because none of them have safe defaults:
 *   - `domain_organic` / `domain_adwords`     → 1000 rows × 10-100u/row =
 *                                                autonomous-cost prohibitive
 *   - `phrase_related` / `phrase_questions`   → require customer-supplied
 *                                                seed keyword (sync has none)
 *   - `domain_rank_history`                    → requires `displayDate`
 *                                                widget-config-specific
 *   - `domain_organic_organic`                 → 200u/row historical;
 *                                                burns budget without consent
 *
 * Adding any of these 6 to autonomous sync would flip an integration's
 * monthly unit consumption from ~30K → 200K+ — that decision must remain
 * widget-config-explicit, never autonomous.
 *
 * **Story 35.1 expansion (9 NEW widget-config-driven endpoints):** the
 * autonomous surface remains EXACTLY 4 endpoints. The 9 new endpoints land
 * ONLY on the proxy-route surface — same boundary as Story 20.5:
 *   - `backlinks` / `backlinks_refdomains` / `backlinks_anchors` /
 *     `backlinks_pages` / `backlinks_tld` / `backlinks_geo` /
 *     `backlinks_refips` — all charge 40u FIXED per call (per
 *     developer.semrush.com/api/v3/analytics/backlinks/ verified 2026-05-19).
 *     With no sane autonomous default (link-lists are widget-scope-specific),
 *     autonomous-sync wiring would burn unit budget without consent.
 *   - `url_organic` / `url_adwords` — require a fully-qualified URL the
 *     autonomous sync has no way to choose (a domain has thousands of URLs).
 *
 * The Trends `/summary` expansion (16 new device-split + per-device
 * engagement + source-split columns) ALSO lands ONLY on the widget-config
 * path — the autonomous Trends call continues to fetch the original 23-col
 * `SEMRUSH_TRENDS_SUMMARY_DEFAULT_COLUMNS` set. Widget configs that want the
 * device-split + source-split columns pass the expanded `export_columns`
 * through the proxy route.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
  EndpointDiscoveryInfo,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import {
  SEMRUSH_API_VERSION,
  SEMRUSH_ANALYTICS_API_BASE,
  SEMRUSH_BACKLINKS_API_BASE,
  SEMRUSH_TRENDS_API_BASE,
  SEMRUSH_BALANCE_URL,
  validateSemrushConnection,
  fetchSemrushDomainRanks,
  fetchSemrushBacklinksOverview,
  fetchSemrushPhraseKdi,
  fetchSemrushTrendsSummary,
  // Story 20.5 — keyword-rank expansion helpers + default-columns constants.
  SEMRUSH_DOMAIN_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ADWORDS_DEFAULT_COLUMNS,
  SEMRUSH_PHRASE_EXPANSION_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_RANK_HISTORY_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_COLUMNS,
  // Story 35.1 — Backlinks expansion + URL Reports default-columns constants.
  SEMRUSH_BACKLINKS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFDOMAINS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_ANCHORS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_PAGES_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_TLD_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_GEO_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFIPS_DEFAULT_COLUMNS,
  SEMRUSH_URL_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_URL_ADWORDS_DEFAULT_COLUMNS,
} from "./api";
import {
  semrushErrorTypeToAdapterCode,
  parseSemrushApiError,
  sanitizeSemrushErrorMessage,
} from "./errors";
import { normalizeSemrushTarget } from "./normalize";
import {
  transformDomainRanksRow,
  transformBacklinksOverviewRow,
  transformPhraseKdiRow,
  transformTrendsSummaryRow,
  transformSemrushCurrencyField,
} from "./transformers";
import {
  checkSemrushUnitBudget,
  checkSemrushTrendsUnitBudget,
  incrementSemrushUnitsUsed,
  incrementSemrushTrendsUnitsUsed,
  SEMRUSH_DAILY_TENANT_UNIT_BUDGET,
} from "./unit-budget";
import type { KVStore } from "../cache";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "semrush" as const;

export {
  SEMRUSH_API_VERSION,
  SEMRUSH_ANALYTICS_API_BASE,
  SEMRUSH_BACKLINKS_API_BASE,
  SEMRUSH_TRENDS_API_BASE,
  SEMRUSH_BALANCE_URL,
};

export const SEMRUSH_PLATFORM_VALUES = ["semrush"] as const;
export type SemrushPlatform = (typeof SEMRUSH_PLATFORM_VALUES)[number];

/**
 * Story 20.9 — canonical GreenLife demo target for cross-SEO snapshot
 * seeding. Mirrors `MOZ_DEMO_SNAPSHOT_TARGET` at `moz.ts:158` per Story
 * 20.8 Pass 2 BH2-8 cross-app-drift-defense convention (single source of
 * truth shared between `demo-seed.ts` INSERT and `widgetPresets.ts`
 * `dataSource.filter.target`).
 */
export const SEMRUSH_DEMO_SNAPSHOT_TARGET = "greenlifewellness.com";

/** Number of monthly snapshots seeded per Story 20.9 AC #6 + AC #7. */
export const SEMRUSH_DEMO_SNAPSHOT_ROW_COUNT = 6;

/**
 * Story 20.6 — single source of truth for synthetic metric keys.
 *
 * Synthetic metrics are computed at preset-expansion time (or via inline
 * aggregation in the renderer) from per-row catalogue data, NOT fetched
 * from a Semrush wire-format API field. They MUST NOT carry an `apiField`
 * — the metric-allowlist tests + SEMRUSH_METRICS shape invariants exempt
 * them from the apiField requirement.
 *
 * Defining the set here (not in the test files) ensures any rename of a
 * synthetic key updates BOTH the runtime check AND the test exemption in
 * lockstep — preventing a future agent from silently breaking the
 * "synthetic must have no apiField" invariant. Mirrors the Ahrefs
 * `AHREFS_SYNTHETIC_METRIC_KEYS` pattern from Story 20.2b.
 */
export const SEMRUSH_SYNTHETIC_METRIC_KEYS = new Set<string>([
  // The AI Visibility Mixed preset's headline metric — fraction of `domain-organic`
  // rows where `hasAiOverview === true`. Float [0, 1].
  "aiOverviewPresenceRate",
  // The Position Changes Distribution preset's 4-bucket aggregates. Each is
  // an Integer count derived at preset-expand time from `domain-organic`
  // rows by `organicPositionDiff` sign + `previousOrganicPosition === null`.
  "positionsNew",
  "positionsImproved",
  "positionsDeclined",
  "positionsLost",
]);

// ============================================================================
// Default `export_columns` per endpoint (AC #4 + AC #13)
// ============================================================================

/** `domain_ranks` — 12 columns per AC #13. */
export const SEMRUSH_DOMAIN_RANKS_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Db", "Dn", "Rk", "Or", "Ot", "Oc", "Ad", "At", "Ac", "Sh", "Sv", "Dt",
];

/** `backlinks_overview` — 14 columns per AC #13. */
export const SEMRUSH_BACKLINKS_OVERVIEW_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "ascore", "total", "domains_num", "urls_num", "ips_num", "ipclassc_num",
  "follows_num", "nofollows_num", "sponsored_num", "ugc_num", "texts_num",
  "images_num", "forms_num", "frames_num",
];

/** `phrase_kdi` — 2 minimum (Ph + Kd) per AC #13. */
export const SEMRUSH_PHRASE_KDI_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "Ph", "Kd",
];

/**
 * Trends `/summary` — 25-column default set (was 23 cols pre-Story-35.1)
 * including the 2026-flagship AI-visibility columns (`ai_assistants`,
 * `ai_search`, `unknown_channel`) and the Story 35.1 social-split additions
 * (`social_organic`, `social_paid`).
 *
 * Story 35.1 note: `social_organic` and `social_paid` are explicitly
 * fetched by the autonomous sync now that they're catalogued metrics. The
 * widget-config-driven trends-summary route can request additional columns
 * (device split, per-device engagement, users breakdown, time-on-site) on
 * top of this default set — see `SEMRUSH_TRENDS_SUMMARY_EXTRA_COLUMNS`.
 */
export const SEMRUSH_TRENDS_SUMMARY_DEFAULT_COLUMNS: ReadonlyArray<string> = [
  "target", "display_date", "country",
  "total_visits", "desktop_visits", "mobile_visits",
  "total_unique_visitors",
  "total_pages_per_visit", "total_avg_visit_duration", "total_bounce_rate",
  "direct", "referral", "social", "search",
  "search_organic", "search_paid", "paid", "mail", "display_ad",
  // Story 35.1 — social-split additions (`social_organic` / `social_paid`)
  // promoted from opt-in to default so the autonomous sync has the full
  // 2026-relevant traffic-source breakdown without widget-config gymnastics.
  "social_organic", "social_paid",
  "ai_assistants", "ai_search", "unknown_channel",
  "accuracy",
];

// Story 20.5 — re-export the 5 new default-columns constants from api.ts so
// the metric-allowlist test (which already imports from `./semrush`) can
// resolve them without changing its import surface.
export {
  SEMRUSH_DOMAIN_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ADWORDS_DEFAULT_COLUMNS,
  SEMRUSH_PHRASE_EXPANSION_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_RANK_HISTORY_DEFAULT_COLUMNS,
  SEMRUSH_DOMAIN_ORGANIC_COMPETITORS_DEFAULT_COLUMNS,
};

// Story 35.1 — re-export the 9 new default-columns constants for the
// Backlinks expansion + URL Reports so the metric-allowlist test can resolve
// them from the same import surface as Story 20.5's arms.
export {
  SEMRUSH_BACKLINKS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFDOMAINS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_ANCHORS_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_PAGES_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_TLD_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_GEO_DEFAULT_COLUMNS,
  SEMRUSH_BACKLINKS_REFIPS_DEFAULT_COLUMNS,
  SEMRUSH_URL_ORGANIC_DEFAULT_COLUMNS,
  SEMRUSH_URL_ADWORDS_DEFAULT_COLUMNS,
};
// SEMRUSH_BACKLINKS_UNIT_COST flows through the api.ts barrel re-export below.

// ============================================================================
// Metric / Dimension catalogues (AC #13)
// ============================================================================

export const SEMRUSH_METRICS: MetricDefinition[] = [
  // ── Domain Overview Group (10) — domain_ranks ──
  { key: "semrushRank", label: "Semrush Rank", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"], apiField: "Rk", description: "Domain's position in Semrush's global rank by organic visibility." },
  { key: "organicKeywords", label: "Organic Keywords", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "Or", description: "Number of keywords the domain ranks for in the database's top 100." },
  { key: "organicTraffic", label: "Organic Traffic", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "Ot", description: "Estimated monthly organic visits." },
  { key: "organicTrafficCost", label: "Organic Traffic Cost", type: "currency", category: "domain-ranks", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "Oc", description: "Estimated equivalent monthly Adwords spend in USD for the same volume of clicks." },
  { key: "adwordsKeywords", label: "Paid Keywords", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "Ad", description: "Number of paid (Adwords) keywords the domain bids on." },
  { key: "adwordsTraffic", label: "Paid Traffic", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "At", description: "Estimated monthly visits from paid ads." },
  { key: "adwordsCost", label: "Paid Cost", type: "currency", category: "domain-ranks", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "Ac", description: "Estimated monthly Adwords spend in USD." },
  { key: "plaKeywords", label: "PLA Keywords", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "Sh", description: "Product Listing Ads keywords the domain bids on." },
  { key: "plaUniques", label: "PLA Unique Products", type: "number", category: "domain-ranks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "Sv", description: "Unique products advertised via PLAs." },
  { key: "database", label: "Database", type: "number", category: "domain-ranks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Db", description: "Regional Semrush database (us, uk, de, ...). Surfaced as a dimension-as-metric for cross-database aggregation." },
  // ── Backlinks Overview Group (8) — backlinks_overview ──
  { key: "authorityScore", label: "Authority Score", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"], apiField: "ascore", description: "Semrush's headline 0-100 link-authority score (peer to Ahrefs DR)." },
  { key: "totalBacklinks", label: "Total Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "total", description: "Total inbound links to the target." },
  { key: "referringDomains", label: "Referring Domains", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "domains_num", description: "Unique domains pointing at the target." },
  { key: "referringIps", label: "Referring IPs", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "ips_num", description: "Unique IP addresses pointing at the target." },
  { key: "referringClassCSubnets", label: "Referring Class-C Subnets", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "ipclassc_num", description: "Unique Class-C IP subnets pointing at the target." },
  { key: "dofollowBacklinks", label: "Dofollow Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "follows_num", description: "Backlinks that pass authority (no rel=\"nofollow\")." },
  { key: "nofollowBacklinks", label: "Nofollow Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "nofollows_num", description: "Backlinks tagged rel=\"nofollow\"." },
  { key: "sponsoredBacklinks", label: "Sponsored Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "sponsored_num", description: "Backlinks tagged rel=\"sponsored\"." },
  // ── Keyword Difficulty Group (3) — phrase_kdi ──
  { key: "keywordPhrase", label: "Keyword Phrase", type: "number", category: "phrase-kdi", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Ph", description: "The keyword being analyzed." },
  { key: "keywordDifficulty", label: "Keyword Difficulty", type: "number", category: "phrase-kdi", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"], apiField: "Kd", description: "0-100 difficulty index — higher = harder to rank organically." },
  { key: "searchIntent", label: "Search Intent", type: "number", category: "phrase-kdi", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "In", description: "0=Commercial, 1=Informational, 2=Navigational, 3=Transactional." },
  // ── Trends Summary Group (9) — trends_summary ──
  { key: "totalVisits", label: "Total Visits", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "total_visits", description: "Total estimated monthly visits via Trends API." },
  { key: "desktopVisits", label: "Desktop Visits", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "desktop_visits", description: "Estimated monthly visits from desktop devices." },
  { key: "mobileVisits", label: "Mobile Visits", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "mobile_visits", description: "Estimated monthly visits from mobile devices." },
  { key: "bounceRate", label: "Bounce Rate", type: "percentage", category: "trends-summary", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "totalVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "total_bounce_rate", description: "Estimated proportion of single-page visits (0..1)." },
  { key: "avgVisitDuration", label: "Avg. Visit Duration", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "totalVisits" }, compatibleWidgets: ["kpi"], apiField: "total_avg_visit_duration", description: "Estimated average visit duration in seconds." },
  { key: "pagesPerVisit", label: "Pages per Visit", type: "number", category: "trends-summary", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "totalVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "total_pages_per_visit", description: "Estimated average pages viewed per visit." },
  { key: "aiAssistantsTraffic", label: "AI Assistants Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "ai_assistants", description: "2026-flagship: estimated visits arriving from AI assistants (ChatGPT, Claude, Gemini, Copilot, Perplexity, ...)." },
  { key: "aiSearchTraffic", label: "AI Search Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "ai_search", description: "2026-flagship: estimated visits arriving from AI search modes (Google AI Mode, AI Overviews, ...)." },
  { key: "unknownChannelTraffic", label: "Unknown Channel Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "unknown_channel", description: "Estimated visits from un-classified traffic channels (a residual catch-all)." },

  // ============================================================================
  // Story 20.5 — +30 metrics across 6 new endpoint groups (AC #1, #16)
  //
  // Per-report-type column-code disambiguation pattern (AC #11):
  //   Semrush re-uses 2-letter column codes across endpoints with DIFFERENT
  //   meanings. `Po` is "organic position" on `domain_organic` rows but "ad
  //   position" on `domain_adwords` rows. `Co` is "organic competition" on
  //   `domain_organic` but "paid competition" on `domain_adwords`.
  //
  //   Therefore EVERY metric here uses lowerCamelCase semantic keys with
  //   per-report-scope disambiguation:
  //     organicPosition vs. adPosition
  //     organicCompetition vs. paidCompetition
  //     organicTrendsSeries vs. paidTrendsSeries
  //
  //   Bare names `position`, `competition`, `phrase`, `keyword`, `trends`,
  //   `traffic`, `cost` are RESERVED (the build-time test asserts). The
  //   `phrase-related` / `phrase-questions` endpoints get a documented
  //   exemption for `competition` ONLY (Phrase reports don't have an
  //   organic-vs-paid split — there's no collision risk).
  // ============================================================================

  // ── Domain Organic Group (9) — domain_organic ──
  { key: "organicPosition", label: "Organic Position", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "domainOrganicSearchVolume" }, compatibleWidgets: ["data-table", "kpi"], apiField: "Po", description: "Current organic SERP position for the keyword (1-100). Per Semrush, AI-Overview citations register as position 1 regardless of standard SERP rank." },
  { key: "previousOrganicPosition", label: "Previous Organic Position", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "domainOrganicSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Pp", description: "Organic position in the prior crawl — used to compute the organicPositionDiff column." },
  { key: "organicPositionDiff", label: "Position Δ", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "domainOrganicSearchVolume" }, compatibleWidgets: ["data-table", "kpi"], apiField: "Pd", description: "Position movement vs. comparison crawl — signed integer (negative = position dropped). The single most-important field for the Story 20.6 widget." },
  { key: "domainOrganicSearchVolume", label: "Search Volume (Organic)", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "kpi"], apiField: "Nq", description: "Average monthly search volume for the keyword in the queried regional database." },
  { key: "domainOrganicCpc", label: "CPC (Organic)", type: "currency", category: "domain-organic", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "domainOrganicSearchVolume" }, compatibleWidgets: ["data-table", "kpi"], apiField: "Cp", description: "Average paid cost-per-click in USD for this keyword (helpful for SEO ROI calc)." },
  { key: "trafficShare", label: "Traffic Share %", type: "percentage", category: "domain-organic", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tr", description: "Estimated share of total domain organic traffic this single keyword drives (0-100 integer)." },
  { key: "trafficCostShare", label: "Traffic Cost Share %", type: "percentage", category: "domain-organic", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tc", description: "Estimated share of total domain traffic-equivalent ad spend this keyword represents (0-100 integer)." },
  { key: "organicCompetition", label: "Organic Competition", type: "number", category: "domain-organic", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Co", description: "Competitive density 0-1 — proportion of advertisers also bidding on this keyword in paid search." },
  { key: "organicTrendsSeries", label: "Organic Trends (12-month)", type: "number", category: "domain-organic", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Td", description: "String-encoded 12-month search-volume trend series (e.g. \"0.42,0.45,0.50,...\")." },

  // ── Domain Adwords Group (5) — domain_adwords ──
  { key: "adPosition", label: "Ad Position", type: "number", category: "domain-adwords", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table", "kpi"], apiField: "Po", description: "Average paid-ad position when the customer's domain bids on this keyword." },
  { key: "previousAdPosition", label: "Previous Ad Position", type: "number", category: "domain-adwords", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Pp", description: "Ad position in the prior crawl." },
  { key: "adPositionDiff", label: "Ad Position Δ", type: "number", category: "domain-adwords", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Pd", description: "Ad-position movement vs. comparison crawl — signed integer." },
  { key: "adwordsBlock", label: "Ad Block", type: "number", category: "domain-adwords", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Ab", description: "Where the ad rendered: top / side / bottom." },
  { key: "paidCompetition", label: "Paid Competition", type: "number", category: "domain-adwords", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Co", description: "Paid-search competitive density 0-1 for this keyword." },
  // Code Review Pass 1 (MED-5): catalogue-completeness for opt-in fields
  // emitted by `transformDomainAdwordsRow`.
  { key: "paidTrendsSeries", label: "Paid Trends (12-month)", type: "number", category: "domain-adwords", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Td", description: "String-encoded 12-month paid-search-volume trend series for the keyword." },
  { key: "paidCompetitorCount", label: "Paid Competitors (per ad)", type: "number", category: "domain-adwords", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Pc", description: "Number of distinct keywords per ad copy (Semrush PC field)." },

  // ── Phrase Related Group (4) — phrase_related ──
  { key: "phraseRelatedSearchVolume", label: "Search Volume (Related)", type: "number", category: "phrase-related", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Nq", description: "Average monthly search volume for the related-phrase candidate." },
  { key: "phraseRelatedCpc", label: "CPC (Related)", type: "currency", category: "phrase-related", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "phraseRelatedSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Cp", description: "Average paid cost-per-click for the related-phrase candidate." },
  { key: "phraseRelatedCompetition", label: "Competition (Related)", type: "number", category: "phrase-related", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Co", description: "Paid-search competitive density 0-1 for the related-phrase candidate (Phrase reports have no organic-vs-paid split — `Co` here is the bare paid-density value)." },
  { key: "phraseRelatedKeywordDifficulty", label: "KD (Related)", type: "number", category: "phrase-related", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table", "kpi"], apiField: "Kd", description: "0-100 organic-rank difficulty for the related-phrase candidate." },

  // ── Phrase Questions Group (3) — phrase_questions ──
  { key: "phraseQuestionsSearchVolume", label: "Search Volume (Question)", type: "number", category: "phrase-questions", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Nq", description: "Average monthly search volume for the question-form keyword." },
  { key: "phraseQuestionsKeywordDifficulty", label: "KD (Question)", type: "number", category: "phrase-questions", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Kd", description: "0-100 organic-rank difficulty for the question-form keyword. AI-friendly question-form keywords are the 2026 differentiator for AI Overview presence." },
  { key: "phraseQuestionsSearchIntent", label: "Search Intent (Question)", type: "number", category: "phrase-questions", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "In", description: "0=Commercial, 1=Informational, 2=Navigational, 3=Transactional." },

  // ── Domain Rank History Group (5) — domain_rank_history ──
  // NOTE: apiField values overlap with domain_ranks (Or, Ot, Oc, Ad, At) but
  // metric KEYS use the `History` suffix to keep keys unique across the
  // `SEMRUSH_METRICS` array.
  { key: "organicKeywordsHistory", label: "Organic Keywords (History)", type: "number", category: "domain-rank-history", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart", "kpi"], apiField: "Or", description: "Time-series count of organic keywords the domain ranks for, by month." },
  { key: "organicTrafficHistory", label: "Organic Traffic (History)", type: "number", category: "domain-rank-history", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart", "kpi"], apiField: "Ot", description: "Time-series organic traffic estimates, by month." },
  { key: "organicTrafficCostHistory", label: "Organic Traffic Cost (History)", type: "currency", category: "domain-rank-history", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["line-chart"], apiField: "Oc", description: "Time-series equivalent monthly Adwords spend (USD) for the same volume of organic clicks." },
  { key: "adwordsKeywordsHistory", label: "Paid Keywords (History)", type: "number", category: "domain-rank-history", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"], apiField: "Ad", description: "Time-series count of paid keywords the domain bids on, by month." },
  { key: "adwordsTrafficHistory", label: "Paid Traffic (History)", type: "number", category: "domain-rank-history", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["line-chart"], apiField: "At", description: "Time-series paid traffic estimates, by month." },

  // ── Domain Organic Competitors Group (4) — domain_organic_organic ──
  { key: "competitionRelevance", label: "Competition Relevance", type: "number", category: "domain-organic-organic", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Cr", description: "0-1 competitor relevance score: higher = more keyword overlap with the customer's domain." },
  { key: "commonKeywordsCount", label: "Common Keywords", type: "number", category: "domain-organic-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Np", description: "Count of keywords where BOTH the customer's domain and the competitor rank in the top 100." },
  { key: "competitorOrganicKeywords", label: "Competitor Organic Keywords", type: "number", category: "domain-organic-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Or", description: "Total organic-keyword count for the competitor domain." },
  { key: "competitorOrganicTraffic", label: "Competitor Organic Traffic", type: "number", category: "domain-organic-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Ot", description: "Estimated monthly organic traffic for the competitor domain." },
  // Code Review Pass 1 (MED-4): catalogue-completeness for fields emitted
  // by `transformDomainOrganicCompetitorsRow`.
  { key: "competitorOrganicTrafficCost", label: "Competitor Organic Traffic Cost", type: "currency", category: "domain-organic-organic", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Oc", description: "Estimated equivalent monthly Adwords spend (USD) for the competitor's organic traffic." },
  { key: "competitorAdwordsKeywords", label: "Competitor Paid Keywords", type: "number", category: "domain-organic-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Ad", description: "Total paid-keyword count for the competitor domain." },

  // ============================================================================
  // Story 20.6 — Synthetic-aggregate metrics (AC #12, #17). 5 catalogue rows.
  //
  // These are NOT wire-format fields — they're computed at preset-expansion
  // time from per-row data (already in the catalogue) and surfaced via the
  // 8 widget presets in Story 20.6 AC #10. They are exempted from the
  // `apiField is non-empty` build-time invariant via
  // `SEMRUSH_SYNTHETIC_METRIC_KEYS`.
  //
  // **Renderer dependency (forward-looking — Pass 2 Edge Hunter flagged):**
  // computing these aggregates from `domain-organic` rows requires a renderer-
  // side reducer: `aiOverviewPresenceRate = count(hasAiOverview === true) /
  // count(domain-organic rows)`; `positionsNew = count(rows where
  // previousOrganicPosition === null AND organicPosition !== null)`;
  // `positionsImproved = count(rows where organicPositionDiff > 0)`;
  // `positionsDeclined = count(rows where organicPositionDiff < 0)`;
  // `positionsLost` = count of dropped keywords (requires a separate channel
  // not in this story's surface — for Story 20.6 the value comes from
  // `baseMetrics["semrush"].positionsLost` until Story 20.7+ wires the
  // position-history endpoint). The 4 buckets are MUTUALLY EXCLUSIVE —
  // Improved/Declined/New are derived from the 50 fixture rows; Lost is a
  // standalone aggregate from baseMetrics. Renderer aggregator is shipped
  // outside this story's scope (Story 20.7+).
  //
  // Bumps catalogue from 64 → 69 metrics; coverage 36.0% → 37.7%.
  // ============================================================================

  // The AI Visibility Mixed preset's headline metric. Computed as
  // `count(domain-organic rows where hasAiOverview === true) / count(domain-organic rows)`.
  // Float [0, 1] — wellness vertical 25-35% per Semrush 2026 SoV methodology.
  { key: "aiOverviewPresenceRate", label: "AI Overview Presence Rate", type: "percentage", category: "domain-organic", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"], apiField: "_synthetic_aggregate", description: "Synthetic — fraction of top-50 organic keywords where the SERP triggers an AI Overview (computed from `Fk` SERP-feature codes including `FK_AIO`). The 2026 Semrush differentiator." },

  // Position Changes Distribution — 4-bucket aggregates per Semrush KB-495.
  // Each is computed at preset-expand time from `domain-organic` rows.
  { key: "positionsNew", label: "Positions — New", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "_synthetic_aggregate", description: "Synthetic — count of keywords newly entering the top-100 (organicPosition defined, previousOrganicPosition null). Per Semrush KB-495." },
  { key: "positionsImproved", label: "Positions — Improved", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "_synthetic_aggregate", description: "Synthetic — count of keywords that moved up in rank (organicPositionDiff > 0). Per Semrush KB-495." },
  { key: "positionsDeclined", label: "Positions — Declined", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "_synthetic_aggregate", description: "Synthetic — count of keywords that moved down in rank (organicPositionDiff < 0). Per Semrush KB-495." },
  { key: "positionsLost", label: "Positions — Lost", type: "number", category: "domain-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "bar-chart"], apiField: "_synthetic_aggregate", description: "Synthetic — count of keywords previously in the top-100 that have now fallen out. Per Semrush KB-495." },

  // ============================================================================
  // Story 35.1 — +80 metrics across 9 new endpoint groups + 1 expanded surface
  // (AC #1, #16). Bumps catalogue from 69 → 149 (81.4% coverage).
  //
  // Naming policy (carry-forward Story 20.5 AC #11 bare-name reservation):
  //   - Bare `position`, `competition`, `phrase`, `keyword`, `trends`, `traffic`,
  //     `cost` are reserved across ALL endpoints.
  //   - Story 35.1 ADDS 3 NEW reserved tokens: `anchor`, `domain`, `referringIp`
  //     (bare keys collide between backlinks endpoints — use the scope-prefixed
  //     variants below: `anchorText`, `referringDomain`, `refIp`).
  // ============================================================================

  // ── Backlinks Overview — catalog-flip-only (5 fields already emitted by
  //    transformBacklinksOverviewRow in Story 20.4 but missing from the
  //    catalogue per Story 35.1 AC #16). Code-touching: catalogue updates
  //    only; transformer is unchanged. ──
  { key: "referringUrls", label: "Referring URLs", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "urls_num", description: "Unique URLs pointing at the target (a single referring domain can have many URLs)." },
  { key: "ugcBacklinks", label: "UGC Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "ugc_num", description: "Backlinks tagged rel=\"ugc\" (user-generated-content link declaration)." },
  { key: "textBacklinks", label: "Text Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "texts_num", description: "Backlinks rendered as text (anchor + URL), the most common link form." },
  { key: "imageBacklinks", label: "Image Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "images_num", description: "Backlinks rendered as images (image alt-text + linked image)." },
  { key: "formBacklinks", label: "Form Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "forms_num", description: "Backlinks attached to HTML form action targets." },
  { key: "frameBacklinks", label: "Frame Backlinks", type: "number", category: "backlinks-overview", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"], apiField: "frames_num", description: "Backlinks rendered inside iframe/frame elements." },

  // ── Backlinks Full List Group (15 default + 7 opt-in = 22 metrics on
  //    `backlinks`) per AC #2 + AC #9 + AC #12.
  //
  // **dataType convention note (Round 1 CR MEDIUM #2):** boolean flag fields
  // (`isNofollow`, `isForm`, etc.) are catalogued as `dataType: "Enum"` to
  // match the existing vocabulary (Currency / Decimal / Duration / Enum /
  // Integer / Percentage — there is no `Boolean` literal in the dataType
  // taxonomy yet). The transformer emits real `boolean | null` values via
  // `coerceSemrushBooleanFlag`; widget renderers should treat the field as
  // tri-state. Free-form string fields (sourceUrl, targetUrl, anchorText)
  // are also catalogued as Enum to match the pre-Story-35.1 convention
  // (see `database`, `keywordPhrase`, `adwordsBlock` precedents in this
  // file). Adding a `Boolean` literal to the dataType taxonomy is a
  // cross-adapter refactor and deferred.
  // ──
  { key: "pageAuthorityScore", label: "Page Authority Score (Backlink)", type: "number", category: "backlinks", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table", "kpi"], apiField: "page_ascore", description: "Semrush 0-100 page-level Authority Score for the source page of the backlink." },
  { key: "responseCode", label: "Source HTTP Status", type: "number", category: "backlinks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "response_code", description: "HTTP status code of the source page (200 healthy, 404/410 broken, 301 redirected)." },
  { key: "sourceUrl", label: "Source URL", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "source_url", description: "URL of the page that contains the backlink." },
  { key: "sourceTitle", label: "Source Page Title", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "source_title", description: "HTML <title> of the source page." },
  { key: "backlinkTargetUrl", label: "Target URL (Backlink)", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "target_url", description: "URL on the customer's site that the backlink points at." },
  { key: "backlinkTargetTitle", label: "Target Page Title (Backlink)", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "target_title", description: "HTML <title> of the customer's target page." },
  { key: "anchorText", label: "Anchor Text", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "anchor", description: "Visible text of the backlink." },
  { key: "isNofollow", label: "Is Nofollow", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "nofollow", description: "True if the link declares rel=\"nofollow\" (does not pass authority)." },
  { key: "isForm", label: "Is Form Action", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "form", description: "True if the link is the action target of an HTML form." },
  { key: "isFrame", label: "Is Frame", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "frame", description: "True if the link is rendered inside an iframe/frame element." },
  { key: "isImageLink", label: "Is Image Link", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "image", description: "True if the link is an image anchor (linked <img> element)." },
  { key: "isSitewide", label: "Is Sitewide", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "sitewide", description: "True if the link appears on every page of the source domain (footer/sidebar links)." },
  { key: "isNewLink", label: "Is New Link", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table", "kpi"], apiField: "newlink", description: "True if Semrush first observed this link within the most recent crawl window — answers \"what backlinks did we earn this month?\"." },
  { key: "isLostLink", label: "Is Lost Link", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table", "kpi"], apiField: "lostlink", description: "True if Semrush observed this link disappearing within the most recent crawl window." },
  { key: "firstSeen", label: "First Seen Date", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "first_seen", description: "Date Semrush first crawled the backlink." },
  // ── Opt-in fields (allowlist OK, not in default to keep payload bounded). ──
  { key: "externalLinksCount", label: "External Links (Source Page)", type: "number", category: "backlinks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "external_num", description: "Total external links on the source page (higher = lower per-link authority dilution)." },
  { key: "internalLinksCount", label: "Internal Links (Source Page)", type: "number", category: "backlinks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "internal_num", description: "Total internal links on the source page." },
  { key: "redirectUrl", label: "Redirect Target", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "redirect_url", description: "If the source URL is a redirect, the final destination URL." },
  { key: "imageUrl", label: "Image URL (Image Link)", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "image_url", description: "URL of the linked image (when the link is an image anchor)." },
  { key: "imageAltText", label: "Image Alt Text", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "image_alt", description: "Alt-text of the linked image — image-link equivalent of anchor text." },
  { key: "sourcePageSize", label: "Source Page Size (bytes)", type: "number", category: "backlinks", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "source_size", description: "Source page byte size at last crawl." },
  { key: "lastSeen", label: "Last Seen Date", type: "number", category: "backlinks", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "last_seen", description: "Date Semrush last observed the backlink active." },

  // ── Backlinks Refdomains Group (8 metrics) per AC #3 + AC #9. ──
  { key: "referringDomainAuthorityScore", label: "Referring Domain Authority Score", type: "number", category: "backlinks-refdomains", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table", "kpi"], apiField: "domain_ascore", description: "Semrush 0-100 Authority Score for the referring domain (the higher, the more powerful the link)." },
  { key: "referringDomain", label: "Referring Domain", type: "number", category: "backlinks-refdomains", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "domain", description: "Hostname of the referring domain (e.g. nytimes.com)." },
  { key: "backlinksFromDomain", label: "Backlinks from Domain", type: "number", category: "backlinks-refdomains", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "backlinks_num", description: "Total count of backlinks the customer has from this referring domain." },
  { key: "referringDomainTrustScore", label: "Referring Domain Trust Score", type: "number", category: "backlinks-refdomains", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "domain_trust_score", description: "Semrush 0-100 Trust Score for the referring domain (signals editorial integrity)." },
  { key: "referringDomainIp", label: "Referring Domain IP", type: "number", category: "backlinks-refdomains", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "ip", description: "IP address of the referring domain at last crawl." },
  { key: "referringDomainCountry", label: "Referring Domain Country", type: "number", category: "backlinks-refdomains", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "country", description: "ISO country code of the referring-domain hosting IP." },
  { key: "referringDomainFirstSeen", label: "Refdomain First Seen", type: "number", category: "backlinks-refdomains", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "first_seen", description: "Date Semrush first observed a link from this referring domain." },
  { key: "referringDomainLastSeen", label: "Refdomain Last Seen", type: "number", category: "backlinks-refdomains", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "last_seen", description: "Date Semrush last observed a link from this referring domain." },

  // ── Backlinks Anchors Group (5 metrics) per AC #4 + AC #9. ──
  { key: "anchorTextAggregate", label: "Anchor Text (Aggregated)", type: "number", category: "backlinks-anchors", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "anchor", description: "Anchor text grouped across all referring links (one row per unique anchor)." },
  { key: "anchorDomainsCount", label: "Anchor — Domains", type: "number", category: "backlinks-anchors", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "domains_num", description: "Count of unique referring domains using this anchor text." },
  { key: "anchorBacklinksCount", label: "Anchor — Backlinks", type: "number", category: "backlinks-anchors", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "backlinks_num", description: "Total backlink count using this anchor text." },
  { key: "anchorFirstSeen", label: "Anchor First Seen", type: "number", category: "backlinks-anchors", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "first_seen", description: "Date Semrush first observed a link with this anchor." },
  { key: "anchorLastSeen", label: "Anchor Last Seen", type: "number", category: "backlinks-anchors", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "last_seen", description: "Date Semrush last observed a link with this anchor." },

  // ── Backlinks Pages Group (4 metrics) per AC #4 + AC #9. ──
  { key: "pageBacklinkSourceUrl", label: "Page Source URL (Pages)", type: "number", category: "backlinks-pages", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "source_url", description: "URL of a page on the customer's site that has incoming backlinks." },
  { key: "pageBacklinksCount", label: "Backlinks to Page", type: "number", category: "backlinks-pages", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "backlinks_num", description: "Total inbound backlinks to this specific page." },
  { key: "pageDomainsCount", label: "Referring Domains to Page", type: "number", category: "backlinks-pages", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "domains_num", description: "Unique referring domains pointing at this specific page." },
  { key: "pageResponseCode", label: "Page HTTP Status", type: "number", category: "backlinks-pages", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "response_code", description: "HTTP status of the customer's page (catches broken pages still earning links)." },

  // ── Backlinks TLD Group (3 metrics) per AC #4 + AC #9. ──
  { key: "tldZone", label: "TLD", type: "number", category: "backlinks-tld", dataType: "Enum", compatibleWidgets: ["data-table", "pie-chart"], apiField: "zone", description: "Top-level domain (e.g. com, org, edu, gov) — useful for spotting toxic ccTLD floods." },
  { key: "tldDomainsCount", label: "TLD — Domains", type: "number", category: "backlinks-tld", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "domains_num", description: "Unique referring domains under this TLD." },
  { key: "tldBacklinksCount", label: "TLD — Backlinks", type: "number", category: "backlinks-tld", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "backlinks_num", description: "Total backlinks from referring domains under this TLD." },

  // ── Backlinks Geo Group (3 metrics) per AC #4 + AC #9. ──
  { key: "geoCountry", label: "Country (Geo)", type: "number", category: "backlinks-geo", dataType: "Enum", compatibleWidgets: ["data-table", "pie-chart"], apiField: "country", description: "ISO country code of the referring-domain hosting infrastructure." },
  { key: "geoDomainsCount", label: "Geo — Domains", type: "number", category: "backlinks-geo", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "domains_num", description: "Unique referring domains hosted in this country." },
  { key: "geoBacklinksCount", label: "Geo — Backlinks", type: "number", category: "backlinks-geo", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "bar-chart"], apiField: "backlinks_num", description: "Total backlinks from referring domains hosted in this country." },

  // ── Backlinks Refips Group (4 metrics) per AC #4 + AC #9. ──
  { key: "refIp", label: "Referring IP (Aggregated)", type: "number", category: "backlinks-refips", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "ip", description: "IP address grouped across all referring domains (spot link-farm clustering)." },
  { key: "refIpCountry", label: "Refip Country", type: "number", category: "backlinks-refips", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "country", description: "ISO country code of the referring IP." },
  { key: "refIpDomainsCount", label: "Refip — Domains", type: "number", category: "backlinks-refips", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "domains_num", description: "Unique referring domains using this IP (multiple domains on one IP signal link-farm)." },
  { key: "refIpBacklinksCount", label: "Refip — Backlinks", type: "number", category: "backlinks-refips", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "backlinks_num", description: "Total backlinks from referring domains on this IP." },

  // ── URL Organic Group (11 metrics) per AC #5 + AC #9. ──
  { key: "urlOrganicKeyword", label: "Keyword (URL Organic)", type: "number", category: "url-organic", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Ph", description: "Keyword the customer's URL ranks for organically." },
  { key: "urlOrganicPosition", label: "URL Organic Position", type: "number", category: "url-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "urlSearchVolume" }, compatibleWidgets: ["data-table", "kpi"], apiField: "Po", description: "Current organic SERP position for the keyword on this specific URL (1-100)." },
  { key: "urlPreviousOrganicPosition", label: "URL Previous Organic Position", type: "number", category: "url-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "urlSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Pp", description: "Organic position in the prior crawl on this URL." },
  { key: "urlOrganicPositionDiff", label: "URL Organic Position Δ", type: "number", category: "url-organic", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "urlSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Pd", description: "Position movement vs. comparison crawl — signed integer (negative = position dropped)." },
  { key: "urlSearchVolume", label: "Search Volume (URL Organic)", type: "number", category: "url-organic", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table", "kpi"], apiField: "Nq", description: "Monthly search volume for the keyword in the queried regional database." },
  { key: "urlOrganicCpc", label: "CPC (URL Organic)", type: "currency", category: "url-organic", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "urlSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Cp", description: "Average paid CPC for the keyword (USD)." },
  { key: "targetUrl", label: "Target URL (Ranking)", type: "number", category: "url-organic", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Ur", description: "The customer's URL Semrush observed ranking for this keyword (matches the input URL when paths align)." },
  { key: "urlTrafficShare", label: "URL Traffic Share %", type: "percentage", category: "url-organic", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tr", description: "Estimated share of total URL traffic this keyword drives (0-100 integer)." },
  { key: "urlTrafficCostShare", label: "URL Traffic Cost Share %", type: "percentage", category: "url-organic", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tc", description: "Estimated share of total URL traffic-equivalent ad spend." },
  { key: "urlOrganicCompetition", label: "URL Organic Competition", type: "number", category: "url-organic", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Co", description: "Competitive density 0-1 for this keyword on this URL." },
  { key: "urlOrganicTrendsSeries", label: "URL Organic Trends (12-month)", type: "number", category: "url-organic", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Td", description: "String-encoded 12-month search-volume trend series for the keyword." },

  // ── URL Adwords Group (8 metrics) per AC #5 + AC #9. ──
  { key: "urlAdKeyword", label: "Keyword (URL Adwords)", type: "number", category: "url-adwords", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Ph", description: "Keyword the customer's URL bids on in paid search." },
  { key: "urlAdPosition", label: "URL Ad Position", type: "number", category: "url-adwords", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { denominator: "urlAdSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Po", description: "Average paid-ad position when the URL bids on this keyword." },
  { key: "urlAdSearchVolume", label: "Search Volume (URL Adwords)", type: "number", category: "url-adwords", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["data-table"], apiField: "Nq", description: "Monthly search volume for the keyword in the queried regional database." },
  { key: "urlAdCpc", label: "CPC (URL Adwords)", type: "currency", category: "url-adwords", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { denominator: "urlAdSearchVolume" }, compatibleWidgets: ["data-table"], apiField: "Cp", description: "Average paid CPC for the keyword (USD)." },
  { key: "urlAdVisibleUrl", label: "Visible URL (Ad)", type: "number", category: "url-adwords", dataType: "Enum", compatibleWidgets: ["data-table"], apiField: "Vu", description: "Display URL shown in the paid ad (may differ from the destination URL)." },
  { key: "urlAdTrafficShare", label: "URL Ad Traffic Share %", type: "percentage", category: "url-adwords", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tr", description: "Estimated share of total URL paid traffic this keyword drives (0-100 integer)." },
  { key: "urlAdTrafficCostShare", label: "URL Ad Traffic Cost Share %", type: "percentage", category: "url-adwords", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Tc", description: "Estimated share of total URL paid-ad spend." },
  { key: "urlAdCompetition", label: "URL Ad Competition", type: "number", category: "url-adwords", dataType: "Decimal", aggregationKind: "ratio", compatibleWidgets: ["data-table"], apiField: "Co", description: "Paid-search competitive density 0-1 for this keyword on this URL." },

  // ============================================================================
  // Story 35.1 Batch 2 — Trends Summary surface expansion (AC #6, #7, #8).
  // +20 metric definitions covering device-split + per-device engagement +
  // unique visitors + traffic-source split + users breakdown. All ride on the
  // existing `trends-summary` endpoint via expanded `export_columns` — NO new
  // helper, NO new route, ZERO additional unit cost.
  // ============================================================================

  // ── Device-split rank/share (5 metrics) per AC #6. ──
  { key: "totalRank", label: "Total Rank (Trends)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"], apiField: "total_rank", description: "Total Semrush traffic rank for the target across all devices." },
  { key: "desktopRank", label: "Desktop Rank (Trends)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"], apiField: "desktop_rank", description: "Semrush traffic rank when restricting to desktop devices." },
  { key: "mobileRank", label: "Mobile Rank (Trends)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "ratio", compatibleWidgets: ["kpi", "line-chart"], apiField: "mobile_rank", description: "Semrush traffic rank when restricting to mobile devices." },
  { key: "desktopShare", label: "Desktop Share", type: "percentage", category: "trends-summary", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "desktopVisits", denominator: "totalVisits" }, compatibleWidgets: ["kpi", "gauge", "pie-chart"], apiField: "desktop_share", description: "Share of total traffic from desktop devices (0..1)." },
  { key: "mobileShare", label: "Mobile Share", type: "percentage", category: "trends-summary", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "mobileVisits", denominator: "totalVisits" }, compatibleWidgets: ["kpi", "gauge", "pie-chart"], apiField: "mobile_share", description: "Share of total traffic from mobile devices (0..1)." },

  // ── Per-device unique visitors (2 metrics) per AC #6. ──
  { key: "desktopUniqueVisitors", label: "Desktop Unique Visitors", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "desktop_unique_visitors", description: "Estimated monthly unique visitors from desktop devices." },
  { key: "mobileUniqueVisitors", label: "Mobile Unique Visitors", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "mobile_unique_visitors", description: "Estimated monthly unique visitors from mobile devices." },

  // ── Per-device pages per visit (2 metrics) per AC #6 + AC #8. ──
  { key: "desktopPagesPerVisit", label: "Desktop Pages per Visit", type: "number", category: "trends-summary", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "desktopVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "desktop_pages_per_visit", description: "Average pages viewed per visit from desktop devices." },
  { key: "mobilePagesPerVisit", label: "Mobile Pages per Visit", type: "number", category: "trends-summary", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "mobileVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "mobile_pages_per_visit", description: "Average pages viewed per visit from mobile devices." },

  // ── Per-device avg visit duration (2 metrics) per AC #6 + AC #8. ──
  { key: "desktopAvgVisitDuration", label: "Desktop Avg. Visit Duration", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "desktopVisits" }, compatibleWidgets: ["kpi"], apiField: "desktop_avg_visit_duration", description: "Average visit duration in seconds from desktop devices." },
  { key: "mobileAvgVisitDuration", label: "Mobile Avg. Visit Duration", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "mobileVisits" }, compatibleWidgets: ["kpi"], apiField: "mobile_avg_visit_duration", description: "Average visit duration in seconds from mobile devices." },

  // ── Per-device bounce rate (2 metrics) per AC #6 + AC #8. ──
  { key: "desktopBounceRate", label: "Desktop Bounce Rate", type: "percentage", category: "trends-summary", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "desktopVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "desktop_bounce_rate", description: "Estimated bounce rate (0..1) for desktop visits." },
  { key: "mobileBounceRate", label: "Mobile Bounce Rate", type: "percentage", category: "trends-summary", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "mobileVisits" }, compatibleWidgets: ["kpi", "gauge"], apiField: "mobile_bounce_rate", description: "Estimated bounce rate (0..1) for mobile visits. CRO-board headline metric." },

  // ── Traffic source split (8 metrics, *Traffic suffix to dodge bare-name
  //    reservation) per AC #7. ──
  { key: "searchTrafficTotal", label: "Search Traffic (Total)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "search", description: "Estimated monthly visits from search engines (organic + paid combined)." },
  { key: "searchOrganicTraffic", label: "Search Organic Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "search_organic", description: "Estimated monthly visits from organic search results." },
  { key: "searchPaidTraffic", label: "Search Paid Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "search_paid", description: "Estimated monthly visits from paid search ads." },
  { key: "paidTrafficTotal", label: "Paid Traffic (Total)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "paid", description: "Estimated monthly visits from any paid source (search + display + social paid)." },
  { key: "socialOrganicTraffic", label: "Social Organic Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "social_organic", description: "Estimated monthly visits from organic social posts (non-paid)." },
  { key: "socialPaidTraffic", label: "Social Paid Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "social_paid", description: "Estimated monthly visits from paid social ads." },
  { key: "mailTraffic", label: "Mail Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "mail", description: "Estimated monthly visits from email (newsletter clicks, transactional emails, etc.)." },
  { key: "displayAdTraffic", label: "Display Ad Traffic", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "pie-chart"], apiField: "display_ad", description: "Estimated monthly visits from display-ad networks." },

  // ── Users breakdown (3 metrics) per AC #8. ──
  { key: "usersTotal", label: "Users (Total)", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "users", description: "Estimated total monthly users for the target." },
  { key: "desktopUsers", label: "Desktop Users", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "desktop_users", description: "Estimated monthly users from desktop devices." },
  { key: "mobileUsers", label: "Mobile Users", type: "number", category: "trends-summary", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"], apiField: "mobile_users", description: "Estimated monthly users from mobile devices." },

  // ── Time-on-site engagement (3 metrics, including 2 per-device) per AC #8. ──
  // `timeOnSite` (`time_on_site`) is the headline value across devices;
  // `desktop_time_on_site` / `mobile_time_on_site` are the device-split
  // variants. Distinct from `avgVisitDuration` because Semrush's catalogue
  // lists both names — kept separate so widget UI can label each variant.
  { key: "timeOnSite", label: "Time on Site (Trends)", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "totalVisits" }, compatibleWidgets: ["kpi"], apiField: "time_on_site", description: "Total time-on-site in seconds across all devices (Trends-API report of summed dwell time)." },
  { key: "desktopTimeOnSite", label: "Desktop Time on Site", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "desktopVisits" }, compatibleWidgets: ["kpi"], apiField: "desktop_time_on_site", description: "Total time-on-site in seconds from desktop visits." },
  { key: "mobileTimeOnSite", label: "Mobile Time on Site", type: "duration", category: "trends-summary", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "mobileVisits" }, compatibleWidgets: ["kpi"], apiField: "mobile_time_on_site", description: "Total time-on-site in seconds from mobile visits." },
];

export const SEMRUSH_DIMENSIONS: DimensionDefinition[] = [
  { key: "target", label: "Target", type: "Content", exampleValues: ["example.com", "subdomain.example.com"] },
  { key: "database", label: "Regional Database", type: "Geo", exampleValues: ["us", "uk", "de", "fr", "es", "br", "ca", "au", "jp"] },
  { key: "country", label: "Country (ISO-3166-1 alpha-2)", type: "Geo", exampleValues: ["US", "GB", "DE", "FR"] },
  // **AC #12/#13 — disambiguated bare-name policy:** the dimension key is
  // `keywordPhrase` (NOT `phrase`) to mirror the metric key on phrase_kdi
  // rows and avoid collision with the 4 reserved bare names. Pass 1 review
  // (High #14) flagged the original `phrase` dimension key for this.
  { key: "keywordPhrase", label: "Keyword Phrase", type: "Search", exampleValues: ["seo tools", "marketing automation"] },
  { key: "displayDate", label: "Display Date", type: "Time", exampleValues: ["2026-04-01", "2026-05-01"] },
  { key: "deviceType", label: "Device Type", type: "Tech", exampleValues: ["desktop", "mobile", "all"] },

  // Story 20.5 — +9 dimensions for the keyword-rank surface (AC #1 / AC #16).
  // Bare names `position` / `phrase` / `competitor` are AVOIDED — every key
  // uses a scope-prefixed form to keep the cross-endpoint dimension surface
  // unambiguous in the widget config UI.
  { key: "domainDimension", label: "Domain", type: "Content", exampleValues: ["example.com", "competitor.com"] },
  { key: "url", label: "URL", type: "Content", exampleValues: ["https://example.com/blog/seo-101"] },
  { key: "keywordRankPosition", label: "Keyword Position", type: "Search", exampleValues: ["1", "2-3", "4-10", "11-20"] },
  { key: "competitorDomainDimension", label: "Competitor Domain", type: "Content", exampleValues: ["competitor.com", "rival.io"] },
  { key: "serpFeature", label: "SERP Feature", type: "Search", exampleValues: ["FK_AIO", "FK0", "FK11"] },
  { key: "dateGranularity", label: "Date Granularity", type: "Time", exampleValues: ["monthly", "daily"] },
  { key: "trendsBucket", label: "Trends Bucket", type: "Time", exampleValues: ["12-month-series"] },
  { key: "positionDiffBucket", label: "Position Δ Bucket", type: "Search", exampleValues: ["improved", "declined", "new", "lost"] },
  { key: "searchIntentDimension", label: "Search Intent", type: "Search", exampleValues: ["commercial", "informational", "navigational", "transactional"] },

  // ============================================================================
  // Story 35.1 — +8 dimensions (AC #1, #16). Catalogue rows 8/10/11/12/13/14/15/22.
  // Scope-prefixed forms used to dodge the bare-name reservation list:
  //   - `region` (free token, no collision)
  //   - `trafficSourceDimension` (NOT bare `traffic_type` — `traffic` is reserved)
  //   - `sourceUrlDimension` / `sourceDomainDimension` (backlinks dimensions)
  //   - `backlinkTargetUrlDimension` (backlinks target URL)
  //   - `anchorTextDimension` (NOT bare `anchor` — reserved by Story 35.1)
  //   - `tldZoneDimension` (TLD)
  //   - `referringIpDimension` (NOT bare `referringIp` — reserved by Story 35.1)
  // ============================================================================
  { key: "region", label: "Region (Geo)", type: "Geo", exampleValues: ["North America", "EMEA", "APAC"] },
  { key: "trafficSourceDimension", label: "Traffic Source (Trends)", type: "Traffic", exampleValues: ["direct", "referral", "social", "search", "paid", "mail", "display_ad"] },
  { key: "sourceUrlDimension", label: "Source URL (Backlinks)", type: "Content", exampleValues: ["https://example.com/blog/post"] },
  { key: "sourceDomainDimension", label: "Source Domain (Backlinks)", type: "Content", exampleValues: ["example.com", "referrer.org"] },
  { key: "backlinkTargetUrlDimension", label: "Target URL (Backlinks)", type: "Content", exampleValues: ["https://customer.com/landing-page"] },
  { key: "anchorTextDimension", label: "Anchor Text (Backlinks)", type: "Links", exampleValues: ["click here", "best seo tools"] },
  { key: "tldZoneDimension", label: "TLD Zone (Backlinks)", type: "Geo", exampleValues: ["com", "org", "edu", "uk", "io"] },
  { key: "referringIpDimension", label: "Referring IP (Backlinks)", type: "Tech", exampleValues: ["192.0.2.1", "203.0.113.42"] },
];

// ============================================================================
// Helpers
// ============================================================================

function getAdapterKv(ctx: AdapterContext): KVStore | undefined {
  // Same shape as Ahrefs `getAdapterKv` — pull a KV-like binding off
  // `ctx.env`. Workers env exposes `KV` directly; tests pass `{ KV: <stub> }`.
  return (ctx.env as { KV?: KVStore } | undefined)?.KV;
}

function emitStructuredLog(event: string, payload: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.log(JSON.stringify({ event, ...payload, timestamp: new Date().toISOString() }));
}

// ============================================================================
// Story 20.6 — fixture-mode helper (AC #9, #15, #16, #21)
// ============================================================================

/**
 * Single source of truth for Semrush fixture-mode data lag.
 * Pass 3 Edge Hunter M1: `dataFreshnessLagHours` (catalogue field) and
 * `dataFinalizedThrough` (timestamp) MUST stay in lockstep — both encode
 * "data is N hours stale". Defining the value here ensures any change
 * updates both fields atomically.
 */
const SEMRUSH_FIXTURE_LAG_HOURS = 24;

/**
 * Load Semrush demo fixture for the given client (Story 20.6).
 *
 * **Mirrors `loadAhrefsFixture` from Story 20.3 verbatim.** Ahrefs is the
 * canonical SEO-tool template; Semrush is the second SEO-tool helper.
 *
 * **Auth-mode-agnostic** (Round 4 First Principles invariant — AC #16): the
 * helper reads `ctx.clientId` + `ctx.tenantId` ONLY. It does NOT depend on
 * `credentials.accessToken` value or `credentials.refreshToken` presence —
 * both can be empty strings in fixture mode. Future Semrush OAuth (Story
 * 20.4d+) lands without forcing changes to this helper.
 *
 * **`ctx.fixtures` is a SERVER-SIDE-ONLY flag** (Pass 2 Blind Hunter #2
 * security invariant): the `ctx.fixtures: true` short-circuit is gated by
 * a server-side `AdapterContext` field that is NEVER set from a client/
 * tenant request. The route handler at `apps/server/src/routes/...`
 * resolves the integration's `demoMode` column from the database
 * (server-trusted) and stamps `ctx.fixtures = true` if-and-only-if the
 * tenant has demo mode enabled. End-user-controllable fields (request
 * body, query params, headers) MUST NOT propagate to `ctx.fixtures`.
 * This invariant is the difference between "fixture mode is a feature"
 * and "fixture mode is a privilege-escalation vector".
 *
 * **Cross-tenant scope safety** (Round 3 Red Team Attack #2 — AC #15): the
 * helper resolves `clientSlug` via `getClientSlug(db, ctx.tenantId, ctx.clientId)`
 * which enforces the `(tenantId, clientId)` scope at the SQL query layer.
 * The KV cache-key prefix is `fixture:semrush:{clientSlug}` — NEVER
 * `fixture:semrush:{tenantId}:{clientSlug}`. This is intentional: fixtures
 * are KEYED by client-slug because the (tenantId, clientId) → clientSlug
 * mapping is enforced at the SQL layer via `getClientSlug`. The KV layer
 * trusts the slug already-resolved.
 *
 * **Demo-slug uniqueness invariant** (Pass 1 Blind Hunter #7 contract):
 * demo-client slugs (e.g., `greenlife-wellness`) are project-wide
 * RESERVED — only ONE tenant can own a given demo-slug at a time. This
 * invariant is enforced at the demo-data layer (`DEMO_CLIENTS` is a
 * compile-time constant; multi-tenant demo-data customization is NOT a
 * supported pattern in Story 20.6). If a future story adds tenant-
 * specific demo customization, the KV key MUST be re-namespaced to
 * `fixture:semrush:{tenantId}:{slug}` AND the upload script updated
 * symmetrically.
 *
 * **Production-cache-write prevention** (Round 3 Red Team Attack #3): the
 * helper reads ONLY `fixture:semrush:{slug}` keys and NEVER writes to ANY
 * KV key. The production cache prefix `semrush:*` is not touched.
 *
 * **Trends API entitlement bypass** (Round 4 ADR-D — story 20.6 NEW invariant):
 * the fixture-mode helper does NOT read the `kv.get("semrush:meta:{integrationId}")`
 * side-channel for `trendsEntitled` / `defaultDatabase`. Instead, it forces
 * `metadata.trendsEntitled = true` and `metadata.defaultDatabase = "us"`
 * UNCONDITIONALLY in fixture mode (the GreenLife fixture demos with full
 * Trends-API-derived AI traffic numbers regardless of any KV-stored metadata).
 *
 * **Force-overwrite `dataFreshnessLagHours: 24`** (Story 20.4 cache TTL
 * convention — at-most-daily crawl cadence): even if the fixture stamps a
 * different value, the helper force-overwrites this field per Semrush
 * stamping convention.
 *
 * **Structured-log telemetry per AC #21** — emits one of
 * `semrush_demo_fixture_loaded` / `_missing` / `_binding_missing` per
 * invocation so the support team can debug missing fixtures from observability
 * tools without source-code access.
 */
async function loadSemrushFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date }
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  // AC #21 — all 3 telemetry events use `console.log` per spec verbatim.
  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "semrush_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "kv",
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Semrush demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "semrush_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "db",
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve Semrush demo client slug",
      retryable: false,
    });
  }

  // Pass 1 review fix — defensive validation that ctx.tenantId + ctx.clientId
  // are non-empty strings BEFORE calling getClientSlug. Round 3 Red Team
  // Attack #2 cross-tenant leak protection: getClientSlug enforces the
  // (tenantId, clientId) scope at the SQL query layer, but a misconfigured
  // caller passing empty strings could match the first row in the table
  // depending on prepared-statement semantics.
  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    return adapterErr({
      code: "permission_denied",
      message:
        "Semrush demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  // Validate date range — reversed ranges silently return zero records and
  // Invalid Date crashes via toISOString. Return adapterErr for a typed
  // AdapterResult instead. (Mirrors Ahrefs Story 20.3 Pass 1 Edge Case
  // Hunter finding.)
  const startTime = dateRange.start?.getTime?.();
  const endTime = dateRange.end?.getTime?.();
  if (
    typeof startTime !== "number" ||
    !Number.isFinite(startTime) ||
    typeof endTime !== "number" ||
    !Number.isFinite(endTime)
  ) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Semrush demo fixture: dateRange.start and dateRange.end must be finite Date instances",
      retryable: false,
    });
  }
  if (startTime > endTime) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Semrush demo fixture: dateRange.start must be on or before dateRange.end",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // AC #15 / Round 3 Red Team Attack #2 — `getClientSlug` enforces the
    // `(tenantId, clientId)` scope at the SQL query layer. We MUST pass
    // both. Bypassing this resolver would silently leak fixture data
    // across tenants.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Semrush demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "semrush",
    clientSlug
  );

  if (metrics === null) {
    console.log(
      JSON.stringify({
        event: "semrush_demo_fixture_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        clientSlug,
        kvKey: `fixture:semrush:${clientSlug}`,
        timestamp: new Date().toISOString(),
      })
    );
    return adapterErr({
      code: "account_not_found",
      message: `Semrush demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end
  );

  // Pass 1 Blind Hunter #8 fix: warn when the date-range filter strips the
  // fixture down to zero records. Otherwise the UI silently renders empty
  // widgets with no diagnostic — the operator can't tell whether the
  // fixture is missing data or whether their date range is too narrow.
  const filteredWarnings = [...(filtered.warnings ?? [])];
  if (filtered.records.length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason: `Semrush demo fixture filtered to zero records for date range ${dateRange.start.toISOString().split("T")[0]} → ${dateRange.end.toISOString().split("T")[0]}. Try a wider window or check the fixture upload.`,
    });
  }

  // Force-overwrite `dataFreshnessLagHours` per Story 20.4 cache-TTL
  // convention (Semrush crawls at-most-daily). Force-overwrite
  // `trendsEntitled = true` and `defaultDatabase = "us"` per AC #9 step 10
  // (Round 4 ADR-D Trends-API entitlement bypass).
  //
  // Pass 1 Edge Hunter fix: also force-overwrite `dataFinalizedThrough` so
  // demos always show a fresh "last finalized" timestamp, not a stale
  // generation-time value baked into the fixture file. Otherwise a
  // months-old fixture would display its original generation date forever.
  //
  // Pass 1 Blind Hunter contract docs (#4): this is a SHALLOW spread by
  // design. AdapterMetrics.metadata is intentionally a flat key→primitive
  // map across all adapters; nested objects are not part of the contract
  // (no adapter currently emits them). If a future story introduces nested
  // metadata (e.g., `entitlements: { trends: false, analyticsV3: true }`),
  // that story is responsible for either (a) flattening before merge, or
  // (b) introducing a deep-merge helper here. The metadata pass-through
  // test verifies preservation of all known fields; new nested fields
  // should be added to that test in lockstep.
  //
  // Pass 2 Blind Hunter #3 fix: `dataFinalizedThrough` is the MIN of two
  // candidates — `Date.now() - 24h` and `dateRange.end - 24h`. This avoids
  // the demo claiming "data is up to yesterday" when the user has narrowed
  // the date range to a back-window that excludes the latest fixture rows.
  // The result: the `dataFinalizedThrough` stamp is never beyond what's
  // actually present in `filtered.records`.
  //
  // Pass 3 Edge Hunter M1 fix: extract `SEMRUSH_FIXTURE_LAG_HOURS` constant
  // so `dataFreshnessLagHours` and `dataFinalizedThrough` stay in lockstep
  // when either is later changed. Both fields encode "data is N hours
  // stale"; updating one without the other would silently disagree.
  const lagMs = SEMRUSH_FIXTURE_LAG_HOURS * 60 * 60 * 1000;
  const finalizedFromNow = Date.now() - lagMs;
  const finalizedFromRange = dateRange.end.getTime() - lagMs;
  const dataFinalizedThrough = new Date(
    Math.min(finalizedFromNow, finalizedFromRange)
  ).toISOString();
  const result: AdapterMetrics = {
    ...filtered,
    warnings: filteredWarnings,
    metadata: {
      ...(filtered.metadata ?? {}),
      dataFreshnessLagHours: SEMRUSH_FIXTURE_LAG_HOURS,
      dataFinalizedThrough,
      trendsEntitled: true,
      defaultDatabase: "us",
    },
  };

  console.log(
    JSON.stringify({
      event: "semrush_demo_fixture_loaded",
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      clientSlug,
      recordCount: result.recordCount,
      durationMs: Date.now() - startedAt,
      timestamp: new Date().toISOString(),
    })
  );

  return adapterOk(result);
}

// ============================================================================
// Adapter implementation (AC #4, #6, #8, #14)
// ============================================================================

export const semrushAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",
  rateLimits: {
    // Per developer.semrush.com/api/introduction/api-usage-restrictions/:
    // 10 RPS / 10 concurrent — real-world steady state for our serial
    // dispatcher is ~60 req/min. 100K/day is a defensive ceiling; Semrush
    // doesn't publish a hard daily cap but units + Trends quotas effectively
    // cap volume.
    requestsPerMinute: 60,
    requestsPerDay: 100_000,
    burstLimit: 10,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 20.6 — fixture-mode short-circuit (AC #9). MUST run BEFORE any
    // production-path validation (target / apiKey / metadata side-channel /
    // unit-budget) so a demo workspace with no live Semrush + API key still
    // renders fixture data without triggering token_invalid /
    // account_not_found / quota errors.
    //
    // Pass 1 Blind Hunter #5 fix: parameter renamed `_dateRange → dateRange`
    // since it IS used (by the fixture short-circuit + via the production
    // path's downstream `Promise.all` orchestration).
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      return loadSemrushFixture(ctx, dateRange);
    }

    const target = ctx.accountId;
    if (!target) {
      return adapterErr({
        code: "account_not_found",
        message: "No Semrush target configured for this integration. Set the target via the integration settings.",
        retryable: false,
      });
    }
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Semrush API key is missing or empty.",
        retryable: false,
      });
    }

    // Read metadata — the route handler attaches `defaultDatabase`
    // + `trendsEntitled` to ctx via a structural cast. **Pass 2 fix (High):**
    // if the caller didn't propagate metadata (test paths, generic
    // dispatchers), fall back to the KV side-channel
    // `semrush:meta:{integrationId}`. Bounded single-get, no API call.
    const kv = getAdapterKv(ctx);
    const ctxMetadata = (ctx as unknown as { metadata?: Record<string, unknown> }).metadata ?? {};
    let metadata: Record<string, unknown> = ctxMetadata;
    if (
      ctxMetadata.trendsEntitled === undefined &&
      kv &&
      ctx.integrationId
    ) {
      try {
        const raw = await kv.get(`semrush:meta:${ctx.integrationId}`);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            // **Code Review Pass 3 (Medium):** guard against JSON values
            // that aren't plain objects — `JSON.parse("null")` returns
            // null; `JSON.parse("true")` returns true. Spreading those
            // throws TypeError. Only spread on real records.
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
              metadata = { ...ctxMetadata, ...(parsed as Record<string, unknown>) };
            }
          } catch {
            // Malformed JSON — keep ctxMetadata defaults.
          }
        }
      } catch {
        // KV read failure must NOT block fetchMetrics.
      }
    }
    const database = (typeof metadata.defaultDatabase === "string" ? metadata.defaultDatabase : "us").toLowerCase();
    const trendsEntitled = metadata.trendsEntitled === true;

    // Per-tenant Analytics-unit circuit-breaker (AC #5/#8).
    if (kv) {
      const budget = await checkSemrushUnitBudget(kv, ctx.tenantId);
      if (budget.exceeded) {
        if (budget.firstBreach) {
          emitStructuredLog("semrush_daily_unit_budget_exceeded", {
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            currentUnits: budget.current,
            limitUnits: budget.limit,
          });
        }
        return adapterErr({
          code: "rate_limited",
          message:
            `Daily Semrush unit budget exceeded (${budget.current}/${budget.limit}). ` +
            `Sync paused until UTC midnight to protect your monthly quota.`,
          retryable: false,
        });
      }
    }

    const records: Array<Record<string, unknown>> = [];
    const warnings: Array<{ reportType: string; reason: string }> = [];
    let totalUnitsAnalytics = 0;
    let totalUnitsTrends = 0;

    const callCtx = { tenantId: ctx.tenantId, integrationId: ctx.integrationId };

    // **Code Review Pass 1 (High #9):** parallelize the 3 Analytics
    // endpoint calls + (gated) Trends call via Promise.all over per-call
    // catch-wrapped promises. Sequential awaits would compound 4 × 15s
    // helper timeout = 60s worst case which exceeds the Cloudflare Workers
    // 30s CPU limit on slow paths. Mirrors the Ahrefs adapter pattern at
    // `ahrefs.ts:2950+`. Each call's `.catch` returns an `adapterErr`-shaped
    // object so a synchronous throw from one helper can't reject the whole
    // Promise.all batch.
    const trackedKeywords = Array.isArray(metadata.trackedKeywords)
      ? (metadata.trackedKeywords as unknown[]).filter(
          (k): k is string => typeof k === "string" && k.length > 0
        )
      : [];

    // **Code Review Pass 2 (High):** Semrush's `domain_ranks` endpoint
    // requires a BARE hostname (no path). `normalizeSemrushTarget` preserves
    // paths on URL inputs (e.g. "https://example.com/blog" → "example.com/blog")
    // because `backlinks_overview` does accept URL targets via target_type=url.
    // For domain_ranks specifically, strip the path so the helper sends
    // `domain=example.com` (Semrush rejects path-prefixed `domain` values
    // with `ERROR 50 :: NOTHING FOUND`).
    const bareDomain = target.split("/")[0]!;
    const safeDr = fetchSemrushDomainRanks({
      apiKey,
      target: bareDomain,
      database,
      exportColumns: SEMRUSH_DOMAIN_RANKS_DEFAULT_COLUMNS,
    }).catch((err) =>
      adapterErr({
        code: "network_error" as const,
        message: sanitizeSemrushErrorMessage(
          `domain_ranks call threw: ${err instanceof Error ? err.message : String(err)}`,
          apiKey
        ),
        retryable: true,
      })
    );
    // **Code Review Pass 3 (High):** `target_type: "root_domain"` requires
    // a bare hostname (no path). Same fix as `domain_ranks` — strip the
    // path. (A future story can support `target_type: "url"` for path-bearing
    // targets if the customer specifically wants per-URL backlinks.)
    const safeBl = fetchSemrushBacklinksOverview({
      apiKey,
      target: bareDomain,
      targetType: "root_domain",
      exportColumns: SEMRUSH_BACKLINKS_OVERVIEW_DEFAULT_COLUMNS,
    }).catch((err) =>
      adapterErr({
        code: "network_error" as const,
        message: sanitizeSemrushErrorMessage(
          `backlinks_overview call threw: ${err instanceof Error ? err.message : String(err)}`,
          apiKey
        ),
        retryable: true,
      })
    );
    const safeKdi = trackedKeywords.length > 0
      ? fetchSemrushPhraseKdi({
          apiKey,
          phrases: trackedKeywords.slice(0, 100),
          database,
          exportColumns: SEMRUSH_PHRASE_KDI_DEFAULT_COLUMNS,
        }).catch((err) =>
          adapterErr({
            code: "network_error" as const,
            message: sanitizeSemrushErrorMessage(
              `phrase_kdi call threw: ${err instanceof Error ? err.message : String(err)}`,
              apiKey
            ),
            retryable: true,
          })
        )
      : null;

    const [drResult, blResult, kdiResult] = await Promise.all([
      safeDr,
      safeBl,
      safeKdi,
    ]);

    if (drResult.success) {
      for (const row of drResult.data.result.rows) {
        // Use `bareDomain` (matches the actual `domain_ranks` query value)
        // not the original URL-with-path target — keeps the record's
        // `target` field consistent with what was queried.
        records.push(transformDomainRanksRow(row, bareDomain));
      }
      totalUnitsAnalytics += drResult.data.estimatedUnits;
      emitStructuredLog("semrush_api_call_units", {
        ...callCtx,
        type: "domain_ranks",
        rows: drResult.data.result.rows.length,
        unitsCharged: drResult.data.estimatedUnits,
      });
    } else {
      emitStructuredLog("semrush_endpoint_warning", { ...callCtx, type: "domain_ranks", code: drResult.error.code, message: drResult.error.message });
      warnings.push({ reportType: "domain-ranks", reason: drResult.error.code });
    }

    if (blResult.success) {
      for (const row of blResult.data.result.rows) {
        records.push(transformBacklinksOverviewRow(row, bareDomain));
      }
      totalUnitsAnalytics += blResult.data.estimatedUnits;
      emitStructuredLog("semrush_api_call_units", {
        ...callCtx,
        type: "backlinks_overview",
        rows: blResult.data.result.rows.length,
        unitsCharged: blResult.data.estimatedUnits,
      });
    } else {
      emitStructuredLog("semrush_endpoint_warning", { ...callCtx, type: "backlinks_overview", code: blResult.error.code, message: blResult.error.message });
      warnings.push({ reportType: "backlinks-overview", reason: blResult.error.code });
    }

    // **AC #13 — silently-truncated trackedKeywords warning.** When the
    // customer configured >100 keywords we can only query the first 100
    // per call; surface a warning so the widget-config UI can prompt them
    // to split into multiple widgets.
    if (trackedKeywords.length > 100) {
      warnings.push({
        reportType: "phrase-kdi",
        reason: "tracked_keywords_truncated_to_100",
      });
    }

    if (kdiResult) {
      if (kdiResult.success) {
        for (const row of kdiResult.data.result.rows) {
          records.push(transformPhraseKdiRow(row, target, database));
        }
        totalUnitsAnalytics += kdiResult.data.estimatedUnits;
        emitStructuredLog("semrush_api_call_units", {
          ...callCtx,
          type: "phrase_kdi",
          rows: kdiResult.data.result.rows.length,
          unitsCharged: kdiResult.data.estimatedUnits,
        });
      } else {
        emitStructuredLog("semrush_endpoint_warning", { ...callCtx, type: "phrase_kdi", code: kdiResult.error.code, message: kdiResult.error.message });
        warnings.push({ reportType: "phrase-kdi", reason: kdiResult.error.code });
      }
    }

    // ── D. trends_summary (gated on Trends entitlement + monthly budget) ──
    if (!trendsEntitled) {
      // Per Round 5 Acceptance Auditor finding (d) — skip Trends entirely
      // and emit a warning so the renderer surfaces the upsell banner.
      emitStructuredLog("semrush_endpoint_warning", {
        ...callCtx,
        type: "trends_summary",
        code: "trends_subscription_required",
        message: "Semrush .Trends API add-on is not entitled on this key. AI-visibility metrics will not be fetched.",
      });
      warnings.push({
        reportType: "trends-summary",
        reason: "trends_subscription_required",
      });
    } else if (kv) {
      const trendsBudget = await checkSemrushTrendsUnitBudget(kv, ctx.integrationId);
      if (trendsBudget.exceeded) {
        emitStructuredLog("semrush_endpoint_warning", {
          ...callCtx,
          type: "trends_summary",
          code: "trends_quota_exhausted",
          message: `Monthly Trends API quota exceeded (${trendsBudget.current}/${trendsBudget.limit}). Sync resumes at the start of next month.`,
        });
        warnings.push({
          reportType: "trends-summary",
          reason: "trends_quota_exhausted",
        });
      } else {
        const trendsResult = await runTrendsSummary(apiKey, target, callCtx);
        if (trendsResult.success) {
          for (const row of trendsResult.data.result.rows) {
            records.push(transformTrendsSummaryRow(row));
          }
          totalUnitsTrends += trendsResult.data.estimatedUnits;
          emitStructuredLog("semrush_api_call_units", {
            ...callCtx,
            type: "trends_summary",
            rows: trendsResult.data.result.rows.length,
            unitsCharged: trendsResult.data.estimatedUnits,
            quota: "trends",
          });
        } else {
          emitStructuredLog("semrush_endpoint_warning", {
            ...callCtx,
            type: "trends_summary",
            code: trendsResult.error.code,
            message: trendsResult.error.message,
          });
          warnings.push({
            reportType: "trends-summary",
            reason: trendsResult.error.code,
          });
        }
      }
    } else {
      // No KV — entitled but budget-check unavailable. Run the call anyway;
      // operator observability will catch overages via cron.
      const trendsResult = await runTrendsSummary(apiKey, target, callCtx);
      if (trendsResult.success) {
        for (const row of trendsResult.data.result.rows) {
          records.push(transformTrendsSummaryRow(row));
        }
        totalUnitsTrends += trendsResult.data.estimatedUnits;
      } else {
        emitStructuredLog("semrush_endpoint_warning", {
          ...callCtx,
          type: "trends_summary",
          code: trendsResult.error.code,
          message: trendsResult.error.message,
        });
        warnings.push({
          reportType: "trends-summary",
          reason: trendsResult.error.code,
        });
      }
    }

    // ── Persist unit-spend telemetry ──
    if (kv) {
      if (totalUnitsAnalytics > 0) {
        await incrementSemrushUnitsUsed(
          kv,
          ctx.tenantId,
          ctx.integrationId,
          totalUnitsAnalytics
        );
      }
      if (totalUnitsTrends > 0) {
        await incrementSemrushTrendsUnitsUsed(
          kv,
          ctx.integrationId,
          totalUnitsTrends
        );
      }
    }

    return adapterOk({
      platform: PLATFORM,
      accountId: target,
      dateRange,
      records,
      recordCount: records.length,
      fetchedAt: new Date(),
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        // Semrush data updated at-most-daily for crawl-driven metrics
        // (Authority Score recalibrates ~weekly per Semrush blog).
        dataFreshnessLagHours: 24,
        unitsConsumedAnalytics: totalUnitsAnalytics,
        unitsConsumedTrends: totalUnitsTrends,
        unitBudgetLimitAnalytics: SEMRUSH_DAILY_TENANT_UNIT_BUDGET,
      },
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Semrush API key is missing or empty.",
        retryable: false,
      });
    }
    const result = await validateSemrushConnection(apiKey);
    if (!result.success) {
      return adapterErr(result.error);
    }
    return adapterOk(true);
  },

  async discoverAccounts(
    ctx: AdapterContext,
    _credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Mirrors Ahrefs / PageSpeed pattern — Semrush has no concept of "ad
    // accounts" or "properties"; the target IS the account. Surface the
    // configured target as a single AccountInfo; empty list when no target.
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterOk([]);
    }
    return adapterOk([
      {
        id: accountId,
        name: accountId,
        platform: PLATFORM,
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return SEMRUSH_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return SEMRUSH_DIMENSIONS;
  },

  /**
   * AC #14 — per-endpoint availability snapshot.
   *
   * The Trends entitlement is read from the integration's metadata JSON
   * (set at connect time per AC #2). Two read paths:
   *
   *   1. **Primary** — `ctx.metadata.trendsEntitled` (route-handler-attached).
   *   2. **Fallback (Pass 2 fix)** — KV side-channel `semrush:meta:{id}`.
   *      Code paths that don't go through the dedicated route handler
   *      (e.g. widget-config UI calling discoverEndpoints generically,
   *      future generic discovery dispatcher) still get the right answer.
   *
   * The KV fallback is a single get with no list scan — bounded I/O, no
   * extra API calls.
   */
  async discoverEndpoints(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    const apiKey = credentials.accessToken;
    if (!apiKey || apiKey.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Semrush API key is missing or empty.",
        retryable: false,
      });
    }
    const ctxMetadata = (ctx as unknown as { metadata?: Record<string, unknown> }).metadata ?? {};
    let trendsEntitled = ctxMetadata.trendsEntitled === true;

    // **Code Review Pass 2 (High AC #14):** if `ctx.metadata` doesn't
    // carry `trendsEntitled` (caller didn't propagate it), fall back to
    // the KV side-channel `semrush:meta:{integrationId}`. This keeps
    // Trends-gating correct for callers that don't go through the route
    // handler's hand-attached cast.
    if (ctxMetadata.trendsEntitled === undefined) {
      const kv = getAdapterKv(ctx);
      if (kv && ctx.integrationId) {
        try {
          const raw = await kv.get(`semrush:meta:${ctx.integrationId}`);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              // **Code Review Pass 3 (Medium):** guard against parsed
              // primitives (`null` / `true` / numbers / strings) that
              // can't have property access — `null.trendsEntitled`
              // throws TypeError otherwise.
              if (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed) &&
                (parsed as { trendsEntitled?: unknown }).trendsEntitled === true
              ) {
                trendsEntitled = true;
              }
            } catch {
              // Malformed JSON — leave trendsEntitled at its default.
            }
          }
        } catch {
          // KV read failure must NOT block discoverEndpoints.
        }
      }
    }

    const trendsMeta =
      !trendsEntitled
        ? {
            subscriptionRequired: ".Trends",
            setupHint:
              "Semrush .Trends is a separate paid add-on (10K units/month basic tier). Enable at https://www.semrush.com/kb/930-trends-api.",
          }
        : {};

    return adapterOk({
      endpoints: [
        // Story 20.4 — 4 endpoint groups.
        { key: "domain_ranks", status: "available" as const },
        { key: "backlinks_overview", status: "available" as const },
        { key: "phrase_kdi", status: "available" as const },
        {
          key: "trends_summary",
          status: trendsEntitled ? "available" as const : "subscription_required" as const,
          ...trendsMeta,
        },
        // Story 20.5 — 6 new keyword-rank endpoints. All `available` on any
        // Business + API plan (no separate sub-tier check needed; Story 20.4
        // validated the plan tier at connect time).
        { key: "domain_organic", status: "available" as const },
        { key: "domain_adwords", status: "available" as const },
        { key: "phrase_related", status: "available" as const },
        { key: "phrase_questions", status: "available" as const },
        { key: "domain_rank_history", status: "available" as const },
        { key: "domain_organic_organic", status: "available" as const },
        // Story 35.1 — 9 new endpoints (Backlinks expansion + URL reports).
        // All `available` on any Business + API plan — the Backlinks API
        // ships with the standard tier per developer.semrush.com/api/v3/
        // analytics/backlinks/ verified 2026-05-19.
        { key: "backlinks", status: "available" as const },
        { key: "backlinks_refdomains", status: "available" as const },
        { key: "backlinks_anchors", status: "available" as const },
        { key: "backlinks_pages", status: "available" as const },
        { key: "backlinks_tld", status: "available" as const },
        { key: "backlinks_geo", status: "available" as const },
        { key: "backlinks_refips", status: "available" as const },
        { key: "url_organic", status: "available" as const },
        { key: "url_adwords", status: "available" as const },
      ],
    });
  },
};

// ============================================================================
// Trends-summary helper — picks the most recent first-of-month
// ============================================================================

/**
 * The Trends API requires `display_date` to be a first-of-month date in
 * `YYYY-MM-01` format. For autonomous syncs we anchor at the most recent
 * COMPLETED first-of-month (one calendar month back from today UTC).
 *
 * Why "one month back": Semrush Trends data lags ~14 days for the previous
 * month per docs. Querying the current month would return either empty or
 * partial data. The `previous-month` anchor matches the Semrush UI's
 * default which is what agency users compare against.
 */
function previousFirstOfMonthIso(now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0-based — previous month is m - 1, with year wrap.
  const date = new Date(Date.UTC(y, m - 1, 1));
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}-01`;
}

async function runTrendsSummary(
  apiKey: string,
  target: string,
  _callCtx: { tenantId: string; integrationId: string }
): Promise<AdapterResult<{ result: { rows: ReadonlyArray<Record<string, string>> }; estimatedUnits: number }>> {
  return fetchSemrushTrendsSummary({
    apiKey,
    targets: [target],
    displayDate: previousFirstOfMonthIso(),
    exportColumns: SEMRUSH_TRENDS_SUMMARY_DEFAULT_COLUMNS,
  }).catch((err) =>
    adapterErr({
      code: "network_error" as const,
      message: sanitizeSemrushErrorMessage(
        `trends_summary call threw: ${err instanceof Error ? err.message : String(err)}`,
        apiKey
      ),
      retryable: true,
    })
  );
}

// Re-exports for the public surface (route handler uses these).
export {
  semrushErrorTypeToAdapterCode,
  parseSemrushApiError,
  sanitizeSemrushErrorMessage,
  validateSemrushConnection,
  normalizeSemrushTarget,
  transformSemrushCurrencyField,
  previousFirstOfMonthIso,
};
